import { prop } from '@ipfabric/utils/fp/prop';
import { makeBoxTree } from 'components/BoxTree/BoxTree';
import type { UseChildrenContextHook } from 'components/BoxTree/BoxTreeHocProps';
import type { SecuritySchemaTreeContext } from 'components/SecuritySchema/SecuritySchemaTreeContext';
import { isHighlightedChainItem } from 'components/SecuritySchema/helpers/isHighlightedChainItem';
import {
  type SecuritySchemaNode,
  type SecuritySchemaNodeComponentProps,
  isComparisonTestNode,
  isErrorNode,
  isEvalTestNode,
  isGroupTestNode,
  isPathLookupOptionsNode,
  isPolicyNode,
  isRuleNode,
  isTrivialTestNode,
  unknownNodeError,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { CComparisonTestNode } from 'components/SecuritySchema/nodeComponents/CComparisonTestNode';
import { CErrorNode } from 'components/SecuritySchema/nodeComponents/CErrorNode';
import { CEvalTestNode } from 'components/SecuritySchema/nodeComponents/CEvalTestNode';
import { CGroupTestNode } from 'components/SecuritySchema/nodeComponents/CGroupTestNode';
import { CPathLookupOptionsNode } from 'components/SecuritySchema/nodeComponents/CPathLookupOptionsNode';
import { CPolicyNode } from 'components/SecuritySchema/nodeComponents/CPolicyNode';
import { CRuleNode } from 'components/SecuritySchema/nodeComponents/CRuleNode';
import { CTrivialTestNode } from 'components/SecuritySchema/nodeComponents/CTrivialTestNode';
import { produce } from 'immer';
import { useMemo } from 'react';
import type { ComponentType } from 'react';
import { isPropOfType } from 'utils/types/isPropOfType';

const HIGHLIGHTED_SUBTREE_LINES_THICKNESS = 3;

const NodeComponent: ComponentType<SecuritySchemaNodeComponentProps> = (
  props,
) => {
  if (isPropOfType(props, 'node', isComparisonTestNode)) {
    return <CComparisonTestNode {...props} />;
  }
  if (isPropOfType(props, 'node', isGroupTestNode)) {
    return <CGroupTestNode {...props} />;
  }
  if (isPropOfType(props, 'node', isErrorNode)) {
    return <CErrorNode {...props} />;
  }
  if (isPropOfType(props, 'node', isRuleNode)) {
    return <CRuleNode {...props} />;
  }
  if (isPropOfType(props, 'node', isPolicyNode)) {
    return <CPolicyNode {...props} />;
  }
  if (isPropOfType(props, 'node', isTrivialTestNode)) {
    return <CTrivialTestNode {...props} />;
  }
  if (isPropOfType(props, 'node', isPathLookupOptionsNode)) {
    return <CPathLookupOptionsNode {...props} />;
  }
  if (isPropOfType(props, 'node', isEvalTestNode)) {
    return <CEvalTestNode {...props} />;
  }
  // absurd checks unfortunately don't work with isPropOfType
  return <CErrorNode {...props} node={unknownNodeError} />;
};

const useChildrenContext: UseChildrenContextHook<
  SecuritySchemaNode,
  SecuritySchemaTreeContext
> = (context, node) =>
  useMemo(() => {
    if (isHighlightedChainItem(node, context)) {
      return produce(context, (draft) => {
        draft.highlightedSubtree = true;
      });
    }
    return context;
  }, [context, node]);

export const SecuritySchemaTree = makeBoxTree<
  SecuritySchemaNode,
  SecuritySchemaTreeContext
>({
  getChildren: prop('children'),
  getChildrenOrientation: (node) =>
    node === null || isRuleNode(node) || isPolicyNode(node)
      ? 'vertical'
      : 'horizontal',
  getChildrenLinesStyle: (node, context) => ({
    className: isGroupTestNode(node)
      ? `security-tree__connection-lines--${node.group}`
      : null,
    thickness: context.highlightedSubtree
      ? HIGHLIGHTED_SUBTREE_LINES_THICKNESS
      : undefined,
  }),
  nodeComponent: NodeComponent,
  useChildrenContext,
});
